function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React, { useEffect, useRef, useState } from 'react';
import { OverlayTrigger, Table as RBTable, Tooltip } from 'react-bootstrap';
import { AddButton, DeleteButton, EditButton } from '@rd-web-markets/shared/dist/util/buttons';
import { Loader } from '../Loader';
import TextFilter from './TextFilter';
import SelectFilter from './SelectFilter';
import { useTranslation } from 'react-i18next';
import tableMethods from './tableMethods';
import DateFilter from './DateFilter';
import MultiColumnSort from './MultiColumnSort';
import TableColumnFilter, { hasFilterableHeaders } from './TableColumnFilter';
import TableColumnSelector from './TableColumnSelector';
import { ToggleExpandCollapseButton } from '../buttons/ToggleExpandCollapseButton';
import useSyncedHorizontalScroll from '../hooks/useSyncedHorizontalScroll';
import './TableStyle.scss';

/**
 * TODO: REFACTOR
 * 
 * @param Array headers
 *  - E.g. [
 *      { text: 'Header 1' sortable: true },
 *      { text: 'Header 2' } // this one is not sortable
 *    ]
 * @param Array rows
 *  - E.g. { columns: [1123, 'Rado' ]}
 * @param Boolean showControls default = true
 *  - Whether to show edit / delete buttons per row.
 * @param Function onEdit
 * @param Function onDelete
 * @param Function onSort (headerIndex, sortOrder) => { ... }
 * @param String title
 * @param Boolean useMultiColumnSorting
 * @param Boolean useFilterSetup
 *
 * Renders a Table.
 */
export default function Table(_ref) {
  let {
    style,
    title = '',
    headers,
    rows,
    showControls = true,
    onEdit,
    onDelete,
    onSort,
    additionalTableControlButtons = [],
    // should contain a list of buttons if such are necessary - e.g. [<Button> ... </Button>]
    useMultiColumnSorting = false,
    useFilterSetup = false,
    tableColumnSelectorProps = null,
    onFilter,
    // required if we want backend text filtering
    textFilterQueryStringParamName,
    // required if we want backend text filtering
    onAddRow = null,
    rowHorizontalPadding,
    // 1|2|3|4|5
    disableDelete,
    isImmutableUnderReview = false,
    displayAddRowBelow = true,
    addRowTextKey = 'add_row',
    tableTdStyle = {},
    rowStyle = {},
    headerStyle = {},
    rowTdStyle = {},
    headerLowerItemStyle,
    headerTopElementStyle,
    //recieves current header row height. 
    // so it can be used to determine what is the header row height after render
    // see ProratasSection for an example
    onHeaderRendered = () => {},
    opts = {
      bordered: null,
      striped: null,
      responsive: true,
      hover: true,
      headerSticky: false,
      headersClass: null
    }
  } = _ref;
  const [textFilters, setTextFilters, mapHeaders] = tableMethods.useTextFiltering(textFilterQueryStringParamName);
  const {
    t
  } = useTranslation();
  const [tableRows, setTableRows] = useState([...rows]);
  const [tableHeaders, setTableHeaders] = useState(mapHeaders(headers));
  const [isTableColumnSelectorExpanded, setIsTableColumnSelectorExpanded] = useState(false);
  const [isMultiColumnSortExpanded, setIsMultiColumnSortExpanded] = useState(false);
  const [isTableColumnFilterExpanded, setIsTableColumnFilterExpanded] = useState(false);
  // If we open the page and the url already has queries, we use these initial texts
  // to fill the inputs with the values from the query string.
  // Required if we have backend text filtering.
  const [initialFilterTexts, setInitialFilterTexts] = useState({});
  const headerRowRef = useRef(null);
  const {
    bottomScrollbarRef,
    onBottomScrollbarScroll,
    onTopScrollbarScroll,
    scrollContentRef,
    topScrollbarContentRef,
    topScrollbarRef,
    updateTopScrollbarWidth
  } = useSyncedHorizontalScroll();
  if (tableHeaders.some(h => h.sortable)) {
    if (tableRows.some(r => !r.key)) {
      throw new Error('If the table has sortable headers, its rows must have a key field and this key should be a unique value based on the rendered data.');
    }
  }
  const filterRowsByText = tableMethods.useTextFilteringMethod({
    tableHeaders,
    setTableHeaders,
    rows,
    setTableRows,
    textFilters,
    setTextFilters,
    onFilter
  });
  const applyColumnFilters = tableMethods.useApplyColumnFilters({
    tableHeaders,
    setTableHeaders,
    rows,
    setTableRows,
    setTextFilters,
    onFilter
  });
  const localColumnSortFunction = tableMethods.useSortRows({
    tableHeaders,
    tableRows,
    setTableRows
  });
  const columnSortFunction = onSort || localColumnSortFunction;
  const localMultiColumnSortFunction = tableMethods.useSortRowsByColumns({
    tableHeaders,
    tableRows,
    setTableRows
  });
  const multiColumnSortFunction = onSort || localMultiColumnSortFunction;
  const optsDefaults = {
    bordered: null,
    striped: null,
    responsive: true,
    hover: true,
    headerSticky: false
  };
  const optsMerged = {
    ...optsDefaults,
    ...opts
  };
  const headersClass = optsMerged.headersClass ? optsMerged.headersClass : 'text-primary app-fs-table-header ' + (optsMerged.headerSticky ? 'sticky' : '');
  const tableOpts = {
    ...optsMerged
  };
  const responsive = tableOpts.responsive;
  const tableResponsiveClassName = responsive === true ? 'table-responsive' : responsive ? `table-responsive-${responsive}` : '';
  delete tableOpts.responsive; // responsive is handled locally to support the synced top scrollbar
  delete tableOpts.headerClass; // removes errors for unexpected attribute names
  delete tableOpts.headersClass; // removes errors for unexpected attribute names
  delete tableOpts.headerSticky; // removes errors for unexpected attribute names
  delete tableOpts.headerStyle; // removes errors for unexpected attribute names

  function measureHeader() {
    if (headerRowRef.current) {
      const columnWidths = Array.from(headerRowRef.current.querySelectorAll('th')).map(c => c.offsetWidth);
      const headerRowHeight = headerRowRef.current.offsetHeight;
      return {
        columnWidths,
        headerRowHeight
      };
    }
    return {};
  }
  useEffect(() => {
    if (headerRowRef.current) {
      const headerMeasurements = measureHeader();
      onHeaderRendered(headerMeasurements);
    }
  }, [onHeaderRendered]);

  // Use this to do calculations on window resize.
  // The use case for this was that I had to create expandable rows with nested tables, where the nested table
  // had to have exactly the same column widhts as the parent table. However this required to make both tables have
  // their table layout set to "fixed". This meant that they would not update their sizes based on their data.
  // However that meant that the child table specifically does not update itself on resizing of the window,
  // because its widths are set from the original parent table widths. For such cases we call onRenderHeader
  // again when the window is resized, and the table is rerendered. Check the claim group ClaimState on Belgium.
  useEffect(() => {
    const handleResize = () => {
      const headerMeasurements = measureHeader();
      onHeaderRendered(headerMeasurements);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [onHeaderRendered]);
  useEffect(() => {
    setTableRows([...rows]);
    setTableHeaders(mapHeaders(headers));
  }, [rows, headers, mapHeaders]);
  useEffect(() => {
    updateTopScrollbarWidth();
  }, [tableHeaders, tableRows, tableResponsiveClassName, updateTopScrollbarWidth]);
  const setTableHeadersSortOrder = index => {
    tableHeaders[index].order = tableHeaders[index].order === 'asc' ? 'desc' : 'asc';
    tableHeaders.forEach((h, i) => {
      if (i !== index) {
        h.order = null;
      }
    });
    setTableHeaders([...tableHeaders]);
    return tableHeaders[index].order;
  };
  const renderSortableHeaderIcon = header => {
    if (!header.order) {
      return /*#__PURE__*/React.createElement("span", {
        className: "material-icons"
      }, "sort");
    }
    return header.order === 'asc' ? /*#__PURE__*/React.createElement("span", {
      className: "material-icons"
    }, "arrow_drop_up") : /*#__PURE__*/React.createElement("span", {
      className: "material-icons"
    }, "arrow_drop_down");
  };
  const renderHeader = (header, index) => {
    const headerClassName = (optsMerged.bordered ? '' : 'border-0') + opts.headerClass ? opts.headerClass : ' font-weight-normal';
    header.attributes ||= {};
    const finalHeaderStyle = {
      ...{
        position: 'relative',
        ...optsMerged.headerStyle,
        verticalAlign: 'text-top',
        width: header.width,
        minWidth: header.minWidth || header.width,
        ...header.styleAttributes
      },
      // default,
      ...header.attributes.style
    };
    header.attributes.style = finalHeaderStyle;
    return header.sortable ? /*#__PURE__*/React.createElement("th", _extends({
      key: index,
      onClick: useMultiColumnSorting ? undefined : () => {
        setTableHeadersSortOrder(index);
        if (typeof columnSortFunction === 'function') {
          columnSortFunction(index, tableHeaders[index].order, header.text);
        }
      },
      className: headerClassName
    }, header.attributes), /*#__PURE__*/React.createElement("div", {
      className: useMultiColumnSorting ? '' : 'cursor-pointer',
      style: headerTopElementStyle || (useFilterSetup ? {} : {
        marginBottom: '40px'
      })
    }, header.text, " ", !useMultiColumnSorting && !header.hideSortIcon && renderSortableHeaderIcon(header)), !useFilterSetup && header.filterableByText && /*#__PURE__*/React.createElement(TextFilter, {
      dataTestId: `table-header-text-filter-${index}`,
      initialValue: header._filterText,
      style: headerLowerItemStyle || {
        position: 'absolute',
        bottom: 10
      },
      onFilter: text => filterRowsByText(text, index)
    }), !useFilterSetup && header.filterableBySelect && /*#__PURE__*/React.createElement(SelectFilter, {
      dataTestId: `table-header-select-filter-${index}`,
      initialValue: header._filterText,
      style: headerLowerItemStyle || {
        position: 'absolute',
        bottom: 10
      },
      onFilter: text => filterRowsByText(text, index),
      options: header.filterOptions
    }), !useFilterSetup && header.filterableByDate && /*#__PURE__*/React.createElement(DateFilter, {
      initialValue: header._filterText,
      style: headerLowerItemStyle || {
        position: 'absolute',
        bottom: 10
      },
      onFilter: text => filterRowsByText(text, index)
    })) : /*#__PURE__*/React.createElement("th", _extends({
      key: index,
      className: headerClassName
    }, header.attributes), header.text, !useFilterSetup && header.filterableByText && /*#__PURE__*/React.createElement(TextFilter, {
      dataTestId: `table-header-text-filter-${index}`,
      initialValue: header._filterText,
      style: {
        position: 'absolute',
        bottom: 10
      },
      onFilter: text => filterRowsByText(text, index)
    }));
  };
  function renderExpandedContentRow(row, index) {
    const colSpan = (showControls ? 1 : 0) + tableHeaders.length;
    return /*#__PURE__*/React.createElement("tr", {
      className: "table-row-expanded"
    }, /*#__PURE__*/React.createElement("td", {
      colSpan: colSpan
    }, row.renderExpandedContent(row)));
  }
  function renderCellContent(_ref2) {
    let {
      colText,
      formattedColText,
      header,
      tooltipTargetTestId
    } = _ref2;
    const tooltipText = typeof header?.tooltipFn === 'function' ? header.tooltipFn(colText) : null;
    const tooltipMinWidth = typeof header?.tooltipWidth === 'number' ? `${header.tooltipWidth}px` : header?.tooltipWidth;
    if (!tooltipText) {
      return formattedColText;
    }
    return /*#__PURE__*/React.createElement(OverlayTrigger, {
      placement: "top",
      overlay: /*#__PURE__*/React.createElement(Tooltip, {
        className: "TableCellTooltip",
        "data-testid": "table-cell-tooltip",
        style: {
          minWidth: tooltipMinWidth,
          '--table-cell-tooltip-min-width': tooltipMinWidth
        }
      }, tooltipText)
    }, /*#__PURE__*/React.createElement("span", {
      "data-testid": tooltipTargetTestId
    }, formattedColText));
  }
  function renderRow(row, index) {
    // add a custom class to the rows when building rows. 
    // can be used for example when a row has unsaved data and you want under this or any other circumstances
    // to apply a custom class to the row.
    // see where used for example in DeminimisSubsidy.jsx
    const customTdClass = row?.customTdClass ? row.customTdClass : '';
    return /*#__PURE__*/React.createElement("tr", {
      key: row.key || `row-${index}`,
      className: row.className,
      style: rowStyle
    }, row.columns.map((colText, i) => {
      const header = tableHeaders[i];
      const formatFn = header?.formatFn;
      const formattedColText = typeof formatFn === 'function' ? formatFn(colText) : colText;
      const rowKey = row.key || `row-${index}`;
      const cellTestId = `table-cell-${rowKey}-${i}`;
      const cellContent = renderCellContent({
        colText,
        formattedColText,
        header,
        tooltipTargetTestId: `${cellTestId}-tooltip-target`
      });
      const tdStyle = {
        ...rowTdStyle,
        ...header?.cellStyleAttributes
      };
      let tdClass = rowHorizontalPadding && i === 0 ? `pl-${rowHorizontalPadding}` : '';
      tdClass = rowHorizontalPadding && !showControls && i === row.columns.length - 1 ? `pe-${rowHorizontalPadding}` : tdClass;
      return /*#__PURE__*/React.createElement("td", {
        "data-testid": cellTestId,
        style: tdStyle,
        className: `${tdClass} ${customTdClass}`,
        key: `col-${i}`
      }, cellContent);
    }), showControls && /*#__PURE__*/React.createElement("td", {
      style: {
        minWidth: '90px',
        ...rowTdStyle
      },
      className: `text-end ${customTdClass} ${rowHorizontalPadding ? `pe-${rowHorizontalPadding}` : ''}`
    }, row.controls && /*#__PURE__*/React.createElement("div", null, row.controls), !row.controls && row.pending && /*#__PURE__*/React.createElement(Loader, null), !row.controls && !row.pending && onEdit && row.showEdit !== false && /*#__PURE__*/React.createElement(EditButton, {
      isImmutableUnderReview: isImmutableUnderReview,
      onClick: () => onEdit(index, row)
    }), !row.controls && !row.pending && onDelete && row.showDelete !== false && /*#__PURE__*/React.createElement(DeleteButton, {
      isImmutableUnderReview: isImmutableUnderReview,
      onClick: () => onDelete(index, row),
      disabled: disableDelete
    })));
  }
  const rowsWithExpandedRows = [];
  tableRows.forEach(row => {
    rowsWithExpandedRows.push(row);
    if (row.expandable && row.expanded) {
      rowsWithExpandedRows.push({
        ...row,
        key: `${row.key}-expanded`,
        isExpandedContentRow: true,
        renderExpandedContent: row.renderExpandedContent
      });
    }
  });
  const shouldRenderMultiColumnSort = useMultiColumnSorting;
  const shouldRenderTableColumnFilter = useFilterSetup && hasFilterableHeaders(tableHeaders);
  const shouldRenderTableColumnSelector = !!tableColumnSelectorProps;
  function renderTableControls() {
    const controls = [];
    if (shouldRenderTableColumnSelector) {
      controls.push(/*#__PURE__*/React.createElement(ToggleExpandCollapseButton, {
        expanded: isTableColumnSelectorExpanded,
        key: "columns",
        onClick: () => setIsTableColumnSelectorExpanded(!isTableColumnSelectorExpanded),
        text: "Columns",
        type: "button"
      }));
    }
    if (shouldRenderMultiColumnSort) {
      controls.push(/*#__PURE__*/React.createElement(ToggleExpandCollapseButton, {
        expanded: isMultiColumnSortExpanded,
        key: "sort",
        onClick: () => setIsMultiColumnSortExpanded(!isMultiColumnSortExpanded),
        text: "Sort",
        type: "button"
      }));
    }
    if (shouldRenderTableColumnFilter) {
      controls.push(/*#__PURE__*/React.createElement(ToggleExpandCollapseButton, {
        expanded: isTableColumnFilterExpanded,
        key: "filter",
        onClick: () => setIsTableColumnFilterExpanded(!isTableColumnFilterExpanded),
        text: "Filter",
        type: "button"
      }));
    }

    // controls.push(...)

    if (controls.length === 0) {
      return null;
    }
    return /*#__PURE__*/React.createElement("div", {
      className: "TableControlsLegend mb-3"
    }, /*#__PURE__*/React.createElement("div", {
      className: "d-flex align-items-center flex-wrap"
    }, /*#__PURE__*/React.createElement("div", {
      className: "border-top",
      style: {
        width: '12px'
      }
    }), /*#__PURE__*/React.createElement("div", {
      className: "TableControlsLegend-ControlButtonsContainer"
    }, !!title && /*#__PURE__*/React.createElement("span", {
      className: "font-weight-bold",
      style: {
        overflowWrap: 'anywhere'
      }
    }, title), controls, /*#__PURE__*/React.createElement("div", {
      className: "TableControlsLegend-AdditionalTableControlButtons"
    }, additionalTableControlButtons.length > 0 ? additionalTableControlButtons : /*#__PURE__*/React.createElement(React.Fragment, null))), /*#__PURE__*/React.createElement("div", {
      className: "border-top flex-grow-1"
    })), shouldRenderTableColumnSelector && /*#__PURE__*/React.createElement(TableColumnSelector, _extends({}, tableColumnSelectorProps, {
      isExpanded: isTableColumnSelectorExpanded
    })), shouldRenderMultiColumnSort && /*#__PURE__*/React.createElement(MultiColumnSort, {
      headers: tableHeaders,
      isExpanded: isMultiColumnSortExpanded,
      onSort: multiColumnSortFunction
    }), shouldRenderTableColumnFilter && /*#__PURE__*/React.createElement(TableColumnFilter, {
      filters: textFilters,
      headers: tableHeaders,
      isExpanded: isTableColumnFilterExpanded,
      onApplyFilters: applyColumnFilters
    }));
  }
  function renderTable() {
    return /*#__PURE__*/React.createElement(RBTable, _extends({}, tableOpts, {
      style: {
        borderCollapse: 'collapse',
        ...style
      }
    }), /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", {
      className: headersClass,
      ref: headerRowRef,
      style: headerStyle
    }, tableHeaders.map((header, i) => renderHeader(header, i)))), /*#__PURE__*/React.createElement("tbody", null, onAddRow && !displayAddRowBelow && /*#__PURE__*/React.createElement("tr", {
      className: "row-add_row"
    }, /*#__PURE__*/React.createElement("td", {
      colSpan: `${tableRows[0]?.columns?.length + 1}`,
      style: tableTdStyle
    }, /*#__PURE__*/React.createElement(AddButton, {
      variant: "link",
      isImmutableUnderReview: isImmutableUnderReview,
      onClick: onAddRow,
      text: t(addRowTextKey)
    }))), rowsWithExpandedRows.map((row, index) => row.isExpandedContentRow ? renderExpandedContentRow(row, index) : renderRow(row, index)), onAddRow && displayAddRowBelow && /*#__PURE__*/React.createElement("tr", {
      className: "row-add_row"
    }, /*#__PURE__*/React.createElement("td", {
      colSpan: `${tableRows[0]?.columns?.length}`,
      style: {
        textAlign: 'left',
        background: 'transparent'
      }
    }, /*#__PURE__*/React.createElement(AddButton, {
      variant: "link",
      isImmutableUnderReview: isImmutableUnderReview,
      onClick: onAddRow,
      text: t(addRowTextKey)
    })))));
  }
  return /*#__PURE__*/React.createElement(React.Fragment, null, renderTableControls(), tableResponsiveClassName ? /*#__PURE__*/React.createElement("div", {
    className: "TableResponsiveScrollbars"
  }, /*#__PURE__*/React.createElement("div", {
    className: `${tableResponsiveClassName} TableResponsiveScrollbars-Top`,
    "data-testid": "table-top-horizontal-scrollbar",
    onScroll: onTopScrollbarScroll,
    ref: topScrollbarRef
  }, /*#__PURE__*/React.createElement("div", {
    className: "TableResponsiveScrollbars-TopContent",
    "data-testid": "table-top-horizontal-scrollbar-content",
    ref: topScrollbarContentRef
  })), /*#__PURE__*/React.createElement("div", {
    className: `${tableResponsiveClassName} TableResponsiveScrollbars-Bottom`,
    "data-testid": "table-bottom-horizontal-scrollbar",
    onScroll: onBottomScrollbarScroll,
    ref: bottomScrollbarRef
  }, /*#__PURE__*/React.createElement("div", {
    "data-testid": "table-horizontal-scroll-content",
    ref: scrollContentRef
  }, renderTable()))) : renderTable());
}